import type { MutableRefObject } from 'react';
import { useEffect, useState } from 'react';
import { getTreeMaxDepth } from './mindmap-derived-model';
import {
  MINDMAP_FIT_TARGET,
  type PendingMindmapFitState,
} from './mindmap-fit-state';
import type { SelectMindmapNodeInput } from './mindmap-navigation';
import type { ParsedMindmap } from './mindmap-types';

const ENTRANCE_DEPTH_DELAY_MS = 80;
const ENTRANCE_BASE_DURATION_MS = 400;

interface UseMindmapEntranceStateInput {
  content: string;
  didInitialFitRef: MutableRefObject<boolean>;
  parsed: ParsedMindmap | null;
  pendingFitRef: MutableRefObject<PendingMindmapFitState>;
  setHoveredNodeId: (nodeId: string | null) => void;
  setSelectedMindmapNode: (input: SelectMindmapNodeInput) => void;
}

export function useMindmapEntranceState(input: UseMindmapEntranceStateInput): boolean {
  const {
    content,
    didInitialFitRef,
    parsed,
    pendingFitRef,
    setHoveredNodeId,
    setSelectedMindmapNode,
  } = input;
  const [isEntranceComplete, setIsEntranceComplete] = useState<boolean>(true);

  useEffect(() => {
    pendingFitRef.current = {
      target: parsed ? MINDMAP_FIT_TARGET.All : MINDMAP_FIT_TARGET.None,
      nodeIds: [],
    };
    didInitialFitRef.current = false;

    if (typeof window === 'undefined') {
      return;
    }

    let entranceTimer: number | null = null;
    const animationFrame = window.requestAnimationFrame(() => {
      if (!parsed) {
        setSelectedMindmapNode({ nodeId: null, fit: false });
        setHoveredNodeId(null);
        setIsEntranceComplete(true);
        return;
      }

      setSelectedMindmapNode({ nodeId: parsed.root.id, fit: false });
      setHoveredNodeId(null);
      setIsEntranceComplete(false);

      const maxDepth = getTreeMaxDepth(parsed.root);
      const entranceWindowMs = maxDepth * ENTRANCE_DEPTH_DELAY_MS + ENTRANCE_BASE_DURATION_MS;
      entranceTimer = window.setTimeout(() => {
        setIsEntranceComplete(true);
      }, entranceWindowMs);
    });

    return () => {
      window.cancelAnimationFrame(animationFrame);
      if (entranceTimer != null) {
        window.clearTimeout(entranceTimer);
      }
    };
  }, [
    content,
    didInitialFitRef,
    parsed,
    pendingFitRef,
    setHoveredNodeId,
    setSelectedMindmapNode,
  ]);

  return isEntranceComplete;
}
